import React from 'react';
import styled from 'styled-components';
import MediaDisplay from './MediaDisplay';

const GridContainer = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing['2xl']} 0;
  
  /* Grid layouts */
  &[data-layout="grid-2"] {
    grid-template-columns: repeat(2, 1fr);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: 1fr;
    }
  }
  
  &[data-layout="grid-3"] {
    grid-template-columns: repeat(3, 1fr);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-template-columns: 1fr;
    }
  }
  
  &[data-layout="grid-4"] {
    grid-template-columns: repeat(4, 1fr);
    
    @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
      grid-template-columns: repeat(3, 1fr);
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      grid-template-columns: 1fr;
    }
  }
  
  /* Side by side layouts */
  &[data-layout="sidebyside-equal"] {
    grid-template-columns: 1fr 1fr;
    align-items: start;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: 1fr;
    }
  }
  
  &[data-layout="sidebyside-60-40"] {
    grid-template-columns: 3fr 2fr;
    align-items: start;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: 1fr;
    }
  }
  
  &[data-layout="sidebyside-40-60"] {
    grid-template-columns: 2fr 3fr;
    align-items: start;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: 1fr;
    }
  }
`;

// Wrapper to ensure consistent card styling for grid items
const GridItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 0;
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.base};
  cursor: ${({ $clickable }) => $clickable ? 'zoom-in' : 'default'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

// Image container within the card
const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

// Caption styling
const Caption = styled.div`
  padding: ${({ theme }) => theme.spacing.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.5;
  text-align: center;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MediaGrid = ({
  images = [],
  layout = 'grid-2',
  alts = [],
  captions = [],
  onImageClick = null,
  globalIndexOffset = 0
}) => {
  // Parse semicolon-separated strings if needed
  const imageArray = typeof images === 'string' 
    ? images.split(';').map(s => s.trim()).filter(Boolean)
    : images;
    
  const altArray = typeof alts === 'string'
    ? alts.split(';').map(s => s.trim()).filter(s => s)
    : Array.isArray(alts) ? alts : [];
    
  const captionArray = typeof captions === 'string'
    ? captions.split(';').map(s => s.trim()).filter(s => s)
    : Array.isArray(captions) ? captions : [];
  
  // Check if we should use MediaDisplay (for backwards compatibility)
  // or our own card layout (for consistent styling)
  const useConsistentCards = true; // Set to false to use MediaDisplay
  
  if (!useConsistentCards) {
    // Original implementation using MediaDisplay
    return (
      <GridContainer data-layout={layout}>
        {imageArray.map((image, index) => (
          <MediaDisplay
            key={index}
            url={image}
            alt={altArray[index] || `Image ${index + 1}`}
            caption={captionArray[index]}
            fullWidth={true}
            type="image"
            inGrid={true}
          />
        ))}
      </GridContainer>
    );
  }
  
  // New implementation with consistent card styling
  return (
    <GridContainer data-layout={layout}>
      {imageArray.map((image, index) => (
        <GridItemWrapper
          key={index}
          $clickable={!!onImageClick}
          onClick={onImageClick ? () => onImageClick(globalIndexOffset + index) : undefined}
        >
          <ImageContainer>
            <img
              src={image}
              alt={altArray[index] || `Image ${index + 1}`}
              loading="lazy"
            />
          </ImageContainer>
          {captionArray[index] && (
            <Caption>{captionArray[index]}</Caption>
          )}
        </GridItemWrapper>
      ))}
    </GridContainer>
  );
};

export default MediaGrid;